import { refs } from '../../core/refs.js';
import { prettifyCode, LARGE_SOURCE_BYTES } from '../../core/wasm.js';

const HISTORY_LIMIT = 100;
const editorHistory = new WeakMap();

export function getLangFor(textarea) {
  if (textarea === refs.htmlEditor) return 'html';
  if (textarea === refs.cssEditor) return 'css';
  if (textarea === refs.jsEditor) return 'js';
  return '';
}

export function getHistory(textarea) {
  if (!editorHistory.has(textarea)) {
    editorHistory.set(textarea, {
      current: textarea.value,
      undo: [],
      redo: []
    });
  }
  return editorHistory.get(textarea);
}

function pushLimited(stack, value) {
  stack.push(value);
  if (stack.length > HISTORY_LIMIT) {
    stack.shift();
  }
}

export function syncHistory(textarea) {
  const history = getHistory(textarea);
  if (textarea.value === history.current) return;
  pushLimited(history.undo, history.current);
  history.current = textarea.value;
  history.redo = [];
}

function emitEditorInput(textarea) {
  textarea.dispatchEvent(new Event('input'));
}

export function applyEditorValue(textarea, value) {
  const history = getHistory(textarea);
  if (value === textarea.value) return;

  pushLimited(history.undo, textarea.value);
  history.current = value;
  history.redo = [];
  textarea.value = value;
  emitEditorInput(textarea);
}

export function undoEditor(textarea) {
  const history = getHistory(textarea);
  if (!history.undo.length) return;

  pushLimited(history.redo, history.current);
  history.current = history.undo.pop();
  textarea.value = history.current;
  emitEditorInput(textarea);
}

export function redoEditor(textarea) {
  const history = getHistory(textarea);
  if (!history.redo.length) return;

  pushLimited(history.undo, history.current);
  history.current = history.redo.pop();
  textarea.value = history.current;
  emitEditorInput(textarea);
}

function collapseTextWhitespace(text) {
  return text.replace(/\s+/g, ' ').trim();
}

function minifyHtml(code) {
  const preserved = [];
  const placeholder = (match) => {
    const token = `___FME_PRESERVE_${preserved.length}___`;
    preserved.push(match);
    return token;
  };

  const minified = code
    .replace(/<(script|style|pre|textarea)\b[\s\S]*?<\/\1>/gi, placeholder)
    .replace(/<!--(?!\[if|<!|\s*ko\b)[\s\S]*?-->/gi, '')
    .replace(/>\s+</g, '><')
    .split(/(<[^>]+>)/g)
    .map((part) => part.startsWith('<') ? part.trim() : collapseTextWhitespace(part))
    .join('')
    .trim();

  return preserved.reduce((html, block, index) => {
    return html.replace(`___FME_PRESERVE_${index}___`, block);
  }, minified);
}

function stripComments(code) {
  let output = '';
  let quote = '';
  let escaped = false;

  for (let i = 0; i < code.length; i++) {
    const char = code[i];
    const next = code[i + 1];

    if (quote) {
      output += char;
      if (escaped) {
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === quote) {
        quote = '';
      }
      continue;
    }

    if (char === '"' || char === "'") {
      quote = char;
      output += char;
      continue;
    }

    if (char === '/' && next === '*') {
      i += 2;
      while (i < code.length && !(code[i] === '*' && code[i + 1] === '/')) {
        i++;
      }
      i++;
      continue;
    }

    output += char;
  }

  return output;
}

function minifyCss(code) {
  return stripComments(code)
    .replace(/\s+/g, ' ')
    .replace(/\s*([{}:;,>+~])\s*/g, '$1')
    .replace(/;}/g, '}')
    .trim();
}

function minifyJs(code) {
  return code
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join(' ');
}

export function minifyCode(code, lang) {
  if (lang === 'html') return minifyHtml(code);
  if (lang === 'css') return minifyCss(code);
  if (lang === 'js') return minifyJs(code);
  return code;
}

/**
 * Formats the editor's contents in place. Returns false without touching the
 * editor (or the Rust formatter) when the buffer is too large to format
 * cheaply from a single click/shortcut; see `LARGE_SOURCE_BYTES`.
 */
export function prettifyEditorCode(textarea, lang) {
  const code = textarea.value;
  if (code.length > LARGE_SOURCE_BYTES) {
    console.warn(
      `[Front-my-End] Skipped formatting ${lang || 'code'}: ${code.length} bytes exceeds the ${LARGE_SOURCE_BYTES} byte format limit.`
    );
    return false;
  }

  const formatted = prettifyCode(code, lang);
  applyEditorValue(textarea, formatted);
  return true;
}
